/**
 * Escalations — the admin home for time-boxed access.
 *
 * Approval authority can come from a directory group, which carries no
 * permission a gate could read, so who may decide is taken from `tenantWide` on
 * the list response and never re-derived from the permission set.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AdminViewHeader from "../components/AdminViewHeader.jsx";
import EmptyState from "../components/EmptyState.jsx";
import EscalationCard from "../components/escalation/EscalationCard.jsx";
import EscalationRequestDialog from "../components/escalation/EscalationRequestDialog.jsx";
import ApproverGroupsPanel from "../components/escalation/ApproverGroupsPanel.jsx";
import EscalationAuditTab from "../components/escalation/EscalationAuditTab.jsx";
import { ACTION_TEXT_CLASS } from "../lib/ui.js";
import { hasPermission, invalidateMyPermissions, useMyPermissions } from "../lib/permissions.js";
import { PERMISSIONS } from "../lib/permissionKeys.js";
import { denialOrRawMessage } from "../lib/denialReason.js";
import { useCurrentUser } from "../lib/userInfo.js";
import { useEndpointNames, useSubjectNames } from "../lib/escalationTargets.js";
import {
    ESCALATION_STATES,
    canRequestEscalation,
    cancelEscalationRequest,
    decideEscalationRequest,
    isLiveEscalation,
    listEscalationRequests,
    revokeEscalationRequest,
} from "../lib/escalationClient.js";

const REQUESTS_PER_PAGE = 5;
const PAGE_BUTTON_CLASS = "border border-[color:var(--line)] bg-transparent px-[10px] py-[5px] text-[11px] text-[color:var(--muted)] hover:text-[color:var(--ink)] disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer";

// A runaway guard, not a product limit. The queues are bounded in practice:
// live windows are capped per analyst, and pending rows leave the queue as they
// are decided.
const QUEUE_PAGE_CAP = 20;

// An approver cannot decide or revoke a row the page never fetched, so the two
// queues drain instead of showing their first page. History is unbounded and
// pages on demand.
async function listQueue(states) {
    const requests = [];
    let pageToken = "";
    for (let page = 0; page < QUEUE_PAGE_CAP; page += 1) {
        // eslint-disable-next-line no-await-in-loop
        const result = await listEscalationRequests({ states, pageToken });
        requests.push(...result.requests);
        pageToken = result.nextPageToken;
        if (!pageToken) break;
    }
    return requests;
}

// The three calls overlap: the unfiltered history page carries pending and live
// rows too. Newest first, matching the order every queue renders in.
function mergeById(requests) {
    const byId = new Map();
    for (const request of requests) {
        byId.set(request.id, request);
    }
    return [...byId.values()].sort(
        (a, b) => (b.createdAt?.getTime() ?? 0) - (a.createdAt?.getTime() ?? 0),
    );
}

function Section({ title, requests, cardProps, hasMore = false, loadingMore = false, onLoadMore }) {
    const sectionRef = useRef(null);
    const [page, setPage] = useState(0);
    const lastPage = Math.max(0, Math.ceil(requests.length / REQUESTS_PER_PAGE) - 1);
    const currentPage = Math.min(page, lastPage);
    const start = currentPage * REQUESTS_PER_PAGE;
    const end = Math.min(start + REQUESTS_PER_PAGE, requests.length);
    const count = `${requests.length}${hasMore ? "+" : ""}`;

    useEffect(() => { setPage((current) => Math.min(current, lastPage)); }, [lastPage]);

    const changePage = (nextPage) => {
        setPage(nextPage);
        sectionRef.current?.scrollIntoView({ block: "start" });
    };

    return (
        <section ref={sectionRef} aria-label={title} aria-busy={loadingMore ? "true" : undefined} className="grid min-w-0 border border-[color:var(--line)]">
            <h2 className="m-0 border-b border-[color:var(--line)] bg-[color:var(--control-bg)] p-[12px] text-[14px] leading-[1.25] font-semibold tracking-[-0.02em] text-[color:var(--ink)]">
                {title} <span className="font-normal tabular-nums text-[color:var(--muted)]">({count})</span>
            </h2>
            <div className="grid min-w-0">
                {requests.slice(start, end).map((request) => (
                    <EscalationCard key={request.id} {...cardProps(request)} />
                ))}
            </div>
            {requests.length > REQUESTS_PER_PAGE || hasMore ? (
                <nav aria-label={`${title} pagination`} className="flex flex-wrap items-center justify-between gap-[8px] border-t border-[color:var(--line)] p-[12px]">
                    <span aria-live="polite" aria-atomic="true" className="text-[11px] tabular-nums text-[color:var(--muted)]">
                        {requests.length ? `${start + 1}-${end} of ${count}` : ""}
                    </span>
                    <div className="ml-auto flex flex-wrap items-center justify-end gap-[6px]">
                        <button type="button" className={PAGE_BUTTON_CLASS} disabled={currentPage === 0 || loadingMore} onClick={() => changePage(currentPage - 1)}>
                            Previous
                        </button>
                        <button type="button" className={PAGE_BUTTON_CLASS} disabled={currentPage === lastPage || loadingMore} onClick={() => changePage(currentPage + 1)}>
                            Next
                        </button>
                        {hasMore && currentPage === lastPage ? (
                            <button type="button" className={PAGE_BUTTON_CLASS} disabled={loadingMore} onClick={onLoadMore}>
                                {loadingMore ? "Loading..." : "Load earlier requests"}
                            </button>
                        ) : null}
                    </div>
                </nav>
            ) : null}
        </section>
    );
}

export default function EscalationsPage({ viewDef }) {
    const caps = useMyPermissions();
    const user = useCurrentUser();
    // Naming an approver group hands approval to everyone in it, so configuring
    // the rota is tenant administration, not something an approver can do.
    const canManageApprovers = hasPermission(caps, PERMISSIONS.ROLES_MANAGE);
    const canRequest = canRequestEscalation(caps);

    const [tab, setTab] = useState("requests");
    const [requests, setRequests] = useState([]);
    // null until the list answers: rendering either audience before then makes
    // the header claim a scope, and "Your requests" flickers on every mount.
    const [tenantWide, setTenantWide] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [historyCursor, setHistoryCursor] = useState("");
    const [error, setError] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [acting, setActing] = useState({});

    // Errors are held raw and turned into copy at render: threading `caps`
    // through the callbacks would rebuild them whenever the posture object
    // changes identity, and `refresh` runs in an effect keyed on itself.
    //
    // The two queues a person acts on are fetched by state, not sliced out of
    // the newest page: an older pending or still-live escalation would
    // otherwise fall off the end once enough newer requests existed, taking
    // approval and early revocation with it. The unfiltered call is only the
    // first page of history, which `loadMore` walks.
    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const [pendingRows, approvedRows, historyPage] = await Promise.all([
                listQueue([ESCALATION_STATES.PENDING]),
                listQueue([ESCALATION_STATES.APPROVED]),
                listEscalationRequests(),
            ]);
            setRequests(mergeById([
                ...pendingRows,
                ...approvedRows,
                ...historyPage.requests,
            ]));
            setHistoryCursor(historyPage.nextPageToken);
            setTenantWide(historyPage.tenantWide);
            setError(null);
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    }, []);

    const loadMore = useCallback(async () => {
        if (!historyCursor) return;
        setLoadingMore(true);
        try {
            const page = await listEscalationRequests({ pageToken: historyCursor });
            setRequests((current) => mergeById([...current, ...page.requests]));
            setHistoryCursor(page.nextPageToken);
            setError(null);
        } catch (err) {
            setError(err);
        } finally {
            setLoadingMore(false);
        }
    }, [historyCursor]);

    useEffect(() => { refresh(); }, [refresh]);

    const applyResult = useCallback((updated) => {
        setRequests((current) => current.map((r) => (r.id === updated.id ? updated : r)));
        invalidateMyPermissions();
    }, []);

    const runAction = useCallback(async (requestId, work) => {
        setActing((current) => ({ ...current, [requestId]: "pending" }));
        try {
            applyResult(await work());
            setActing((current) => {
                const { [requestId]: _done, ...rest } = current;
                return rest;
            });
        } catch (err) {
            setActing((current) => ({ ...current, [requestId]: err }));
        }
    }, [applyResult]);

    const endpointIds = useMemo(
        () => requests.map((r) => r.target.endpointId).filter(Boolean),
        [requests],
    );
    const endpointNames = useEndpointNames(
        endpointIds,
        hasPermission(caps, PERMISSIONS.ENDPOINT_METADATA_READ),
    );
    const subjects = useMemo(
        () => requests.map((r) => r.target.subject).filter(Boolean),
        [requests],
    );
    const subjectNames = useSubjectNames(
        subjects,
        hasPermission(caps, PERMISSIONS.DIRECTORY_DATA_READ),
    );

    const { live, pending, history } = useMemo(() => {
        const now = Date.now();
        return {
            live: requests.filter((r) => isLiveEscalation(r, now)),
            pending: requests.filter((r) => r.state === ESCALATION_STATES.PENDING),
            history: requests.filter(
                (r) => r.state !== ESCALATION_STATES.PENDING && !isLiveEscalation(r, now),
            ),
        };
    }, [requests]);

    const cardProps = useCallback((request) => ({
        request,
        endpointNames,
        subjectNames,
        isOwn: Boolean(user?.userId) && request.requesterUserId === user.userId,
        canDecide: tenantWide === true,
        busy: acting[request.id] === "pending",
        error: acting[request.id] && acting[request.id] !== "pending"
            ? denialOrRawMessage(acting[request.id], caps)
            : null,
        onDecide: (decision) => runAction(request.id, () => decideEscalationRequest({
            requestId: request.id, ...decision,
        })),
        onCancel: () => runAction(request.id, () => cancelEscalationRequest(request.id)),
        onRevoke: () => runAction(request.id, () => revokeEscalationRequest(request.id)),
    }), [endpointNames, subjectNames, user, tenantWide, acting, runAction, caps]);

    const headerActions = canRequest ? (
        <div className="relative flex min-h-[38px] shrink-0 items-stretch overflow-hidden border border-b-0 border-[color:var(--line)] bg-[color:var(--control-bg)]">
            <button
                type="button"
                onClick={() => setDialogOpen(true)}
                className="inline-flex items-center px-[10px] text-[color:var(--muted)] hover:text-[color:var(--ink)] transition-colors cursor-pointer"
            >
                <span className={`${ACTION_TEXT_CLASS} text-[color:var(--muted)]`}>Request access</span>
            </button>
        </div>
    ) : null;

    const tabs = [
        { id: "requests", label: "Requests" },
        // The trail is only readable by an approver, and `tenantWide` is the one
        // signal that covers a directory-group approver too.
        ...(tenantWide === true ? [{ id: "audit", label: "Audit" }] : []),
        ...(canManageApprovers ? [{ id: "approvers", label: "Approvers" }] : []),
    ].map((entry) => ({
        ...entry,
        active: entry.id === tab,
        dot: entry.id === tab ? "bg-[color:var(--green)]" : undefined,
        onSelect: () => setTab(entry.id),
    }));

    return (
        <>
            <AdminViewHeader
                title={viewDef?.label ?? "Escalations"}
                scope={tenantWide === null
                    ? null
                    : tenantWide ? "Organization" : "Your requests"}
                tabs={tabs.length > 1 ? tabs : null}
                actions={headerActions}
            />
            <div
                aria-busy={loading ? "true" : undefined}
                className="flex-1 min-h-0 min-w-0 overflow-y-auto py-[12px]"
            >
                {tab === "approvers" ? (
                    <ApproverGroupsPanel />
                ) : tab === "audit" ? (
                    <EscalationAuditTab
                        requests={requests}
                        endpointNames={endpointNames}
                        subjectNames={subjectNames}
                    />
                ) : (
                    <div className="grid gap-[24px] pb-[16px]">
                        {error ? (
                            <p className="m-0 border border-red-400/40 bg-red-400/10 px-[10px] py-[8px] text-[11px] leading-[1.4] text-red-400">
                                {denialOrRawMessage(error, caps)}
                            </p>
                        ) : null}

                        {pending.length ? (
                            <Section title="Pending requests" requests={pending} cardProps={cardProps} />
                        ) : null}

                        {live.length ? (
                            <Section title="Active access" requests={live} cardProps={cardProps} />
                        ) : null}

                        {history.length || historyCursor ? (
                            <Section
                                title="History"
                                requests={history}
                                cardProps={cardProps}
                                hasMore={Boolean(historyCursor)}
                                loadingMore={loadingMore}
                                onLoadMore={loadMore}
                            />
                        ) : null}

                        {!loading && !requests.length ? (
                            <EmptyState
                                className="my-[12px]"
                                title="No access requests"
                                note={canRequest
                                    ? "Request temporary access for an investigation."
                                    : null}
                            />
                        ) : null}
                    </div>
                )}
            </div>

            <EscalationRequestDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onSubmitted={refresh}
            />
        </>
    );
}
